import { useEffect, useState } from 'react';
import { Palette } from 'config';
import { Close, Tune, Copy } from 'assets/icons';
import { useNotifications } from './useNotifications';
import * as S from './NotificationSidePanel.styled';

const SEVERITY = {
  error: { color: Palette.Red._400, symbol: '!' },
  info: { color: Palette.PrimaryGray._400, symbol: 'i' },
  warning: { color: Palette.Orange._400, symbol: 'i' },
};

export const NotificationSidePanel = ({ dateFrom$, dateTo$ }) => {
  const {
    notifications: fetchedNotifications,
    isLoading,
    isError,
  } = useNotifications({ from: dateFrom$?.value, to: dateTo$?.value });

  const [notifications, setNotifications] = useState([]);

  // Keep a local copy so dismiss / clear-all stay responsive without refetching.
  useEffect(() => {
    setNotifications(fetchedNotifications);
  }, [fetchedNotifications]);

  const handleDismiss = (id) => setNotifications((prev) => prev.filter((n) => n.id !== id));
  const handleClearAll = () => setNotifications([]);
  const handleCopy = (text) => navigator.clipboard?.writeText(text);

  const renderList = () => {
    if (isLoading) {
      return <S.Empty>Loading notifications…</S.Empty>;
    }
    if (isError) {
      return <S.Empty>Couldn’t load notifications.</S.Empty>;
    }
    if (notifications.length === 0) {
      return <S.Empty>No notifications</S.Empty>;
    }

    return notifications.map((notification) => {
      const { color, symbol } = SEVERITY[notification.severity] ?? SEVERITY.info;

      return (
        <S.Card key={notification.id}>
          <S.Marker $color={color}>{symbol}</S.Marker>
          <S.CardBody>
            <S.CardContent>
              <S.CardHeader>
                <S.CardTitle>{notification.title}</S.CardTitle>
                <S.CardDate>{notification.date}</S.CardDate>
              </S.CardHeader>
              {notification.message && <S.CardMessage>{notification.message}</S.CardMessage>}

              {notification.campaign && (
                <S.CampaignBlock>
                  <S.CampaignLabel>Campaign:</S.CampaignLabel>
                  <S.CampaignName>{notification.campaign.name}</S.CampaignName>
                  <S.CampaignId>
                    <span>ID:</span>
                    <span>{notification.campaign.id}</span>
                    <S.CopyButton
                      type='button'
                      aria-label='Copy campaign ID'
                      onClick={() => handleCopy(notification.campaign.id)}
                    >
                      <Copy />
                    </S.CopyButton>
                  </S.CampaignId>
                </S.CampaignBlock>
              )}

              {notification.changedBy && (
                <S.CardMeta>Changed by {notification.changedBy}</S.CardMeta>
              )}
            </S.CardContent>

            <S.CloseButton
              type='button'
              aria-label='Dismiss notification'
              onClick={() => handleDismiss(notification.id)}
            >
              <Close />
            </S.CloseButton>
          </S.CardBody>
        </S.Card>
      );
    });
  };

  return (
    <S.Wrap>
      <S.Header>
        <S.HeaderTitle>
          <S.Title>Notifications</S.Title>
          <S.IconButton type='button' aria-label='Notification settings'>
            <Tune />
          </S.IconButton>
        </S.HeaderTitle>
        <S.ClearAllButton
          type='button'
          onClick={handleClearAll}
          disabled={notifications.length === 0}
        >
          Clear All
        </S.ClearAllButton>
      </S.Header>

      <S.List>{renderList()}</S.List>
    </S.Wrap>
  );
};
